import styles from './AccountEmailCard.module.css';

import type { ChainFamily } from 'src/blockchain';
import type { ApiSignedChallenge } from 'src/types/wallet';
import type { ApiChangeEmailReferences } from 'src/types/userAuth';

import classNames from 'classnames';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { useUserSession } from 'src/hooks/useUserSession';
import { useWalletSecondFactor, WalletNotConnectedError } from 'src/hooks/useWalletSecondFactor';

import { isTelegramWebApp } from 'src/utils/telegram';
import { normalizeLoginCode } from 'src/utils/emailLogin';
import { checkNewEmail, newEmailErrorCopy } from 'src/utils/emailChange';
import {
  otpReferenceOf,
  shortWalletAddress,
  isSameWalletAddress,
  securityErrorMessage,
} from 'src/utils/securityError';

import { WalletButton, isChainFamily, DEFAULT_FAMILY } from 'src/blockchain';
import { useChangeEmail, useAccountWallet, useConfirmChangeEmail } from 'src/queries';

import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';

type Step = 'view' | 'new' | 'code';

/**
 * The buyer's "Account" block on the Profile screen: shows the account email and the hardened
 * change flow (spec §4.4).
 *
 * Two codes, not one: api mails one to the address the account is moving to and one to the address
 * currently on it, and confirming needs both. That second code is what stops a stolen session from
 * quietly evicting the owner — it lands in the owner's mailbox, and the letter says so.
 *
 * On top of that, an account with a linked wallet signs the change with that wallet — the factor a
 * session thief cannot produce. A purely-email buyer has no wallet to sign with and api lets them
 * through on the two codes alone, so this screen shows no signature step at all for them.
 *
 * The account email lives on the account session (`/user/auth/me`). With no session there is no
 * account email to manage, so we show a hint instead of a form.
 */
export const AccountEmailCard = () => {
  const navigate = useNavigate();
  const { isLoggedIn, account } = useUserSession();
  const changeMutation = useChangeEmail();
  const confirmMutation = useConfirmChangeEmail();

  // An account with a wallet must sign; a purely-email account has none to offer (spec §2, the
  // honest boundary). `primaryWalletId` is what api itself keys the requirement on.
  const needsSignature = !!account?.primaryWalletId;

  // Which wallet api will accept — the account's primary one, which is not necessarily the wallet
  // currently connected in this browser.
  const { data: accountWallet } = useAccountWallet(needsSignature);
  const primaryWalletAddress = accountWallet?.walletAddress;

  // The family is that wallet's stored `chain_type`, which api verifies the signature against —
  // not the build default (app#243, the cabinet's rule since sales#151). Without it an account
  // whose primary wallet is EVM read a sentence naming its 0x address next to a TRON connect
  // button, and could not change its email at all. One family drives both halves, the connect
  // button and the challenge; a family api names that this storefront does not know falls back
  // to the default, which is how this card behaved before.
  const signingFamily = isChainFamily(accountWallet?.chainType)
    ? accountWallet.chainType
    : DEFAULT_FAMILY;

  const { sign, canSign, address: connectedAddress } = useWalletSecondFactor(signingFamily);
  const isTMA = isTelegramWebApp();

  const [step, setStep] = useState<Step>('view');
  const [newEmail, setNewEmail] = useState('');
  const [references, setReferences] = useState<ApiChangeEmailReferences | null>(null);
  const [newEmailCode, setNewEmailCode] = useState('');
  const [currentEmailCode, setCurrentEmailCode] = useState('');
  const [rejectedReference, setRejectedReference] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState('');
  const [changed, setChanged] = useState(false);

  if (!isLoggedIn || !account) {
    return (
      <Card>
        <Header />
        <Stack gap={3}>
          <Typography color="lightWhite" variant="small">
            Sign in to see and manage your account email.
          </Typography>
          <Button title="Sign in" variant="white" onClick={() => navigate('/auth', { state: { from: '/profile' } })} />
        </Stack>
      </Card>
    );
  }

  const currentEmail = account.email;

  // A hint, not a gate: a wallet extension happily stays on a different account, and finding that
  // out from a 403 after signing is a worse experience than being told before.
  const isWrongWallet =
    needsSignature &&
    canSign &&
    !!primaryWalletAddress &&
    !isSameWalletAddress(connectedAddress, primaryWalletAddress);

  const startChange = () => {
    setError('');
    setChanged(false);
    setNewEmail('');
    setStep('new');
  };

  const resetCodes = () => {
    setNewEmailCode('');
    setCurrentEmailCode('');
    setReferences(null);
    setRejectedReference(null);
  };

  const cancel = () => {
    setError('');
    resetCodes();
    setStep('view');
  };

  const handleSendCodes = async () => {
    setError('');
    const check = checkNewEmail(currentEmail, newEmail);
    if (!check.ok) {
      setError(newEmailErrorCopy(check.reason));
      return;
    }
    try {
      const issued = await changeMutation.mutateAsync({ newEmail: check.email });
      setNewEmail(check.email);
      resetCodes();
      setReferences(issued);
      setStep('code');
    } catch (err) {
      setError(securityErrorMessage(err, 'Could not send the codes. Please try again.'));
    }
  };

  const handleConfirm = async () => {
    if (!references) return;
    setError('');
    setRejectedReference(null);

    if (!currentEmailCode.trim() || !newEmailCode.trim()) {
      setError('Enter both codes — one from each mailbox.');
      return;
    }

    setIsConfirming(true);
    try {
      let signedChallenge: ApiSignedChallenge | undefined;

      if (needsSignature) {
        signedChallenge = await sign('email_change');
      }

      await confirmMutation.mutateAsync({
        ...references,
        // Sent as the api issued them — capitals — whatever the phone keyboard typed.
        newEmailCode: normalizeLoginCode(newEmailCode),
        currentEmailCode: normalizeLoginCode(currentEmailCode),
        signedChallenge,
      });

      resetCodes();
      setChanged(true);
      setStep('view');
    } catch (err) {
      if (err instanceof WalletNotConnectedError) {
        setError(err.message);
        return;
      }
      // Points the message at the code api actually refused — with two in the form, "wrong code"
      // alone would leave the buyer guessing which one to retype.
      setRejectedReference(otpReferenceOf(err) ?? null);
      setError(securityErrorMessage(err, 'Could not confirm the change. Please try again.'));
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Card>
      <Header />

      {step === 'view' ? (
        <Stack gap={3}>
          <Stack gap={1}>
            <Typography color="lightWhite" variant="small">
              Email
            </Typography>
            <Typography color="white" fontWeight="600">
              {currentEmail}
            </Typography>
          </Stack>

          {changed ? (
            <div className={styles.success}>
              <Typography color="white" variant="small">
                Your account email is now {currentEmail}.
              </Typography>
            </div>
          ) : null}

          <Button title="Change email" variant="white" onClick={startChange} />
        </Stack>
      ) : null}

      {step === 'new' ? (
        <Stack gap={3}>
          <Typography color="lightWhite" variant="small">
            We will send a code to the new address and a second code to {currentEmail}
            {needsSignature ? ', and ask your wallet to sign the change' : ''}. All of them are
            needed — that is what keeps someone else from moving your account away from you.
          </Typography>

          <Stack gap={1}>
            <Typography color="lightWhite" variant="small">
              New email
            </Typography>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              className={styles.input}
              placeholder="you@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </Stack>

          {error ? <ErrorText>{error}</ErrorText> : null}

          <Button
            title="Send codes"
            variant="accent"
            onClick={handleSendCodes}
            loading={changeMutation.isPending}
            disabled={changeMutation.isPending}
          />
          <button type="button" className={styles.linkButton} onClick={cancel}>
            <Typography color="lightWhite" variant="small">
              Cancel
            </Typography>
          </button>
        </Stack>
      ) : null}

      {step === 'code' ? (
        <Stack gap={3}>
          <Typography color="lightWhite" variant="small">
            We emailed a code to{' '}
            <Typography color="white" fontWeight="600" variant="small">
              {newEmail}
            </Typography>{' '}
            and a code to{' '}
            <Typography color="white" fontWeight="600" variant="small">
              {currentEmail}
            </Typography>
            . Enter both to confirm.
          </Typography>

          <CodeInput
            label={`Code sent to ${currentEmail}`}
            value={currentEmailCode}
            onChange={setCurrentEmailCode}
            invalid={!!rejectedReference && rejectedReference === references?.currentEmailReference}
          />

          <CodeInput
            label={`Code sent to ${newEmail}`}
            value={newEmailCode}
            onChange={setNewEmailCode}
            invalid={!!rejectedReference && rejectedReference === references?.newEmailReference}
          />

          {needsSignature ? (
            <EmailChangeSignature
              family={signingFamily}
              primaryWalletAddress={primaryWalletAddress}
              connectedAddress={connectedAddress}
              canSign={canSign}
              isWrongWallet={isWrongWallet}
              isTMA={isTMA}
            />
          ) : null}

          {error ? <ErrorText>{error}</ErrorText> : null}

          <Button
            title={needsSignature ? 'Sign & confirm' : 'Confirm'}
            variant="accent"
            onClick={handleConfirm}
            loading={isConfirming}
            disabled={isConfirming || (needsSignature && !canSign)}
          />
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              setError('');
              resetCodes();
              setStep('new');
            }}
          >
            <Typography color="lightWhite" variant="small">
              Use a different email
            </Typography>
          </button>
        </Stack>
      ) : null}
    </Card>
  );
};

interface EmailChangeSignatureProps {
  /** The family of the account's primary wallet — the one api verifies the signature against. */
  family: ChainFamily;
  primaryWalletAddress?: string;
  connectedAddress?: string;
  canSign: boolean;
  isWrongWallet: boolean;
  isTMA: boolean;
}

/**
 * The wallet half of an email change, for an account that has a wallet: which wallet signs, the
 * button to connect it, and a warning when another one is connected. The connect button offers
 * wallets of `family`, the same family the challenge is signed with — so the sentence naming an
 * EVM address sits next to an EVM connect button, not a TRON one (app#243).
 */
export const EmailChangeSignature = ({
  family,
  primaryWalletAddress,
  connectedAddress,
  canSign,
  isWrongWallet,
  isTMA,
}: EmailChangeSignatureProps) => (
  <Stack gap={2}>
    <Typography color="lightWhite" variant="small">
      This change is also signed by the wallet linked to your account
      {primaryWalletAddress ? ` (${shortWalletAddress(primaryWalletAddress)})` : ''}.
    </Typography>

    {!canSign ? (
      <Stack gap={2}>
        <Typography color="lightWhite" variant="small">
          Connect that wallet to confirm the change.
        </Typography>
        <WalletButton family={family} />
        {/* Never hide the connect button inside Telegram: which wallets work there depends on the
            chain the app was built for (a TON wallet would run inside the mini-app, browser
            extensions do not — and the builds today carry no TON), so this is a hint about what
            to do if connecting fails, the same one the Linked wallets card gives. */}
        {isTMA ? (
          <div className={styles.warning}>
            <Typography color="white" variant="small">
              If your wallet can&apos;t connect here, open the app in Chrome or Safari —
              Telegram&apos;s built-in browser blocks most wallets.
            </Typography>
          </div>
        ) : null}
      </Stack>
    ) : null}

    {isWrongWallet ? (
      <div className={styles.warning}>
        <Typography color="white" variant="small">
          The connected wallet ({shortWalletAddress(connectedAddress)}) is not the one linked to
          this account ({shortWalletAddress(primaryWalletAddress)}). Switch accounts in your wallet,
          or the signature will be refused.
        </Typography>
      </div>
    ) : null}
  </Stack>
);

const CodeInput = ({
  label,
  value,
  onChange,
  invalid,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  invalid: boolean;
}) => (
  <Stack gap={1}>
    <Typography color="lightWhite" variant="small">
      {label}
    </Typography>
    <input
      type="text"
      inputMode="text"
      autoCapitalize="characters"
      autoCorrect="off"
      spellCheck={false}
      autoComplete="one-time-code"
      aria-label={label}
      aria-invalid={invalid}
      className={classNames(styles.input, { [styles.inputInvalid]: invalid })}
      placeholder="AB12"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </Stack>
);

const Card = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.card}>
    <Stack gap={3}>{children}</Stack>
  </div>
);

const Header = () => (
  <Typography as="h1" variant="title3" fontWeight="600" color="white">
    Account
  </Typography>
);

const ErrorText = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.error}>
    <Typography color="danger" variant="small">
      {children}
    </Typography>
  </div>
);
